/**
 * Quotes, onto the rail and into the action box (docs/features/
 * order-to-invoice/40_quotes_module_spec.md §10; doc 27 for the box). The
 * rail is quotes' own steps (Draft, Published, Customer decision, Order),
 * decided by quotes and carried on the view (object contract; One E spec 13
 * §3.4): this only draws them.
 *
 * Every sentence the business owns is the view's (status labels, the closing
 * line, the timeline, "Replaces …", "Price held from …"); this only places
 * those facts. The box is composed here because the Q1 view carries no box
 * rows: while the customer decides it waits on them, and a quote email that
 * did not go is the one thing to do (the view's `is_email_failed`).
 */
import { extractFieldErrors } from "../data/validation";
import type {
  QuoteLifecycleStep,
  QuoteLineView,
  QuoteVersionView,
  QuoteView,
} from "../data/quote-types";
import { formatShortDateOrDash } from "./formatters";
import {
  alternativesRowWords,
  draftChecksOf,
  quantitiesRowWords,
} from "./quote-draft-checks";
import type { LifecycleRail, RailLink, RailStep } from "./lifecycle-rail";
import { orderStepFailure } from "./order-to-invoice-presentation";

const short = formatShortDateOrDash;

/** The quote's chain, in order; only a draft shows the first step. */
export const QUOTE_RAIL_STEPS = [
  "draft",
  "published",
  "decision",
  "order",
] as const;
export type QuoteRailStep = (typeof QUOTE_RAIL_STEPS)[number];

export function isQuoteRailStep(id: string | null): id is QuoteRailStep {
  return id !== null && (QUOTE_RAIL_STEPS as readonly string[]).includes(id);
}

/**
 * Who a quote is for, as a sentence names them: its contact, or "the
 * customer" on a channel's draft nobody has given a contact yet (merch spec
 * 12: staff choose one by editing the draft, before it can be published).
 */
export function contactName(quote: Pick<QuoteView, "contact">): string {
  return quote.contact?.name ?? "the customer";
}

/** ".01": a line's number on its quote, as its reference ends. */
export function quoteLineNo(
  quote: Pick<QuoteView, "quote_no">,
  line: QuoteLineView,
): string {
  return line.line_ref.startsWith(quote.quote_no)
    ? line.line_ref.slice(quote.quote_no.length)
    : `.${String(line.line_no).padStart(2, "0")}`;
}

/** "A · Bath", "B", or "Version 2" while it has no label yet. */
export function versionWords(v: QuoteVersionView): string {
  const label = v.label ?? `Version ${v.position}`;
  return v.name ? `${label} · ${v.name}` : label;
}

/**
 * Who accepted for the customer, and why, on one line: "Accepted for the customer by Sam Okafor · On the customer's
 * instruction · ref CALL-9 · "Dev rang…"". Null when the customer accepted themselves.
 */
export function acceptedOnBehalfWords(quote: QuoteView): string | null {
  const a = quote.accepted_on_behalf;
  if (!a) return null;
  return [
    `Accepted for the customer by ${a.by}`,
    a.reason_label,
    a.source_ref && `ref ${a.source_ref}`,
    a.note && `\u201c${a.note}\u201d`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** When the latest decision reminder went, from the timeline (newest first); null before any. */
export function lastRemindedAt(quote: QuoteView): string | null {
  return quote.timeline.find((e) => e.kind === "reminded")?.occurred_at ?? null;
}

/** The timeline's own sentence for a failed quote email. */
function emailFailedLabel(quote: QuoteView): string {
  return (
    quote.timeline.find((e) => e.kind === "send_email_failed")?.label ??
    "The quote email could not be sent"
  );
}

// ── The rail ──────────────────────────────────────────────────────────────

/** Where a step leads: the order a quote became, or the quote that replaced it. */
function railLinkOf(ref: QuoteLifecycleStep["link"]): RailLink | null {
  if (!ref) return null;
  if (ref.object === "orders.order") return { kind: "order", orderNo: ref.id };
  if (ref.object === "quotes.quote") return { kind: "quote", quoteNo: ref.id };
  return { kind: "object", object: ref.object, id: ref.id };
}

/**
 * The quote's rail: quotes' own steps (`get_quote_lifecycle_steps`, the
 * view's `steps`) as the rail draws them. Every word and state is quotes';
 * the step's id is its rail id (`quotes.decision` → `decision`, the `?step=`
 * value).
 */
export function railFromQuote(quote: QuoteView): LifecycleRail {
  return {
    steps: quote.steps.map((step): RailStep => ({
      id: step.step.slice(step.step.indexOf(".") + 1),
      name: step.label,
      state: step.state,
      problem: step.problem ?? null,
      status: step.status_lines,
      alert: step.alert ?? null,
      link: railLinkOf(step.link),
      detail: [],
    })),
  };
}

// ── The action box (doc 27) ───────────────────────────────────────────────

export interface QuoteToDoRow {
  key: string;
  tier: "late" | "at_risk" | null;
  verb: string;
  thing: string;
  reason: string;
}

export interface QuoteWaitingRow {
  key: string;
  party: string;
  verb: string;
  thing: string;
  when: string;
}

/**
 * What the open quote needs, and what it waits on. A closed quote has no
 * rows: its box is the view's closing line (doc 27, a closed document).
 */
export function quoteActionBox(quote: QuoteView): {
  toDo: QuoteToDoRow[];
  waiting: QuoteWaitingRow[];
  next: string;
} {
  if (quote.status === "draft") {
    const checks = draftChecksOf(quote);
    return {
      toDo: [
        // A channel's draft: what its owner decides before publishing (merch spec 12 §6).
        ...(checks.quantities.length > 0
          ? [
              {
                key: "quantities",
                tier: null,
                verb: "Choose",
                thing: quantitiesRowWords(checks.quantities.length),
                reason:
                  "Promo Office's price list has quantities the team did not quote: offer the ones worth offering",
              },
            ]
          : []),
        ...(checks.alternatives.length > 0
          ? [
              {
                key: "alternatives",
                tier: null,
                verb: "Decide on",
                thing: alternativesRowWords(checks.alternatives.length),
                reason:
                  "it repeats an article: make it a quantity option, keep it as an extra line, or remove it",
              },
            ]
          : []),
        {
          key: "publish",
          tier: null,
          verb: "Publish",
          thing: quote.quote_no,
          reason: quote.contact
            ? `a draft, seen by staff only: publish it to fix its prices, then share it with ${quote.contact.name}`
            : "a draft with no contact yet: choose the customer's contact, then publish it",
        },
      ],
      waiting: [],
      next: `published, then shared with ${contactName(quote)}`,
    };
  }
  if (quote.status !== "published")
    return { toDo: [], waiting: [], next: quote.closing_label ?? "" };
  const reminded = lastRemindedAt(quote);
  if (!quote.last_shared_at && !quote.is_email_failed)
    return {
      toDo: [
        {
          key: "share",
          tier: null,
          verb: "Share",
          thing: quote.quote_no,
          reason: `published, but ${contactName(quote)} has not been sent the link`,
        },
      ],
      waiting: [],
      next: `shared with ${contactName(quote)}, who then decides by ${short(quote.expires_at)}`,
    };
  return {
    toDo: quote.is_email_failed
      ? [
          {
            key: "email-failed",
            tier: "at_risk",
            verb: "Contact",
            thing: contactName(quote),
            reason: `${emailFailedLabel(quote)}: send it to them yourself`,
          },
        ]
      : [],
    waiting: [
      {
        key: "decision",
        party: contactName(quote),
        verb: "Decide on",
        thing: quote.quote_no,
        when: [
          `Published ${short(quote.published_at)}`,
          reminded && `reminded ${short(reminded)}`,
          `expires ${short(quote.expires_at)}`,
        ]
          .filter(Boolean)
          .join(" · "),
      },
    ],
    next: `the customer's decision, by ${short(quote.expires_at)}`,
  };
}

// ── Refusals ──────────────────────────────────────────────────────────────

export interface QuoteStepFailure {
  /** The banner: a count when inputs are highlighted, and the server's words for the rest. */
  summary: string;
  /** The backend's message per refused input, by its request path (`versions.1.label`). */
  fieldErrors: Record<string, string>;
}

/**
 * The inputs a refusal can name: a version's label or name, by its index in the request, a publish's expiry, a
 * chosen quantity's quantity, price or buy price (by its index in the request), or a draft's new owner, contact or
 * delivery address.
 */
const INPUT_FIELD =
  /^(versions\.\d+\.(label|name)|expires_at|quantities\.\d+\.(quantity|price_pence|cost_pence)|owner_user_id|contact_person_id|address_id)$/;

/**
 * What to show when a step on a quote is refused: a field-tagged 400 binds to
 * its inputs (api-validation.md) and is summarised by count; a refusal of
 * the whole set (`versions`: "Version labels are unique on a line") and
 * anything else (a 409 "no longer open", a step no longer offered) is the
 * server's words.
 */
export function quoteStepFailure(err: unknown): QuoteStepFailure {
  const fieldErrors: Record<string, string> = {};
  const unbound: string[] = [];
  for (const e of extractFieldErrors(err)) {
    if (INPUT_FIELD.test(e.field)) fieldErrors[e.field] ??= e.message;
    else unbound.push(e.message);
  }
  const count = Object.keys(fieldErrors).length;
  const counted =
    count === 0
      ? null
      : count === 1
        ? "One field needs correcting."
        : `${count} fields need correcting.`;
  if (!counted && unbound.length === 0)
    return { summary: orderStepFailure(err).summary, fieldErrors };
  return {
    summary: [...unbound, counted].filter(Boolean).join(" "),
    fieldErrors,
  };
}
